<script>
  import ArrayEditor from './ArrayEditor.svelte';
  
  export let question;
  export let updateWorkingQuestion;

  let availableQuestions, fieldLabelsType, isReference, isCustom, hasNone, showEditor;
  
  // Get available questions for reference
  function getAvailableQuestions() {
    const currentQuestionIndex = parseInt(question.id?.split('--')[1]) || 0;
    const questions = [];
    
    for (let i = 0; i < currentQuestionIndex; i++) {
      questions.push({
        index: i,
        id: `question--${i}`,
        label: `Question ${i}`,
        singleValue: `__answer${i}__`,
        allValue: `__answers${i}__`,
        manyValue: `__manyAnswers${i}__`
      });
    }
    
    return questions;
  }
  
  function getFieldLabelsType() {
    if (!question.fieldLabels) return 'none';
    if (Array.isArray(question.fieldLabels)) return 'custom';
    if (typeof question.fieldLabels === 'string') return 'reference';
    return 'none';
  }
  
  function setFieldLabelsType(type) {
    switch (type) {
      case 'none':
        updateWorkingQuestion('fieldLabels', undefined);
        break;
      case 'custom':
        updateWorkingQuestion('fieldLabels', ['Field 1', 'Field 2']);
        break;
      case 'reference':
        updateWorkingQuestion('fieldLabels', '__priorAnswers__');
        break;
    }
  }
  
  function updateCustomFieldLabels(newLabels) {
    updateWorkingQuestion('fieldLabels', newLabels);
  }
  
  function updateReferenceFieldLabels(reference) {
    updateWorkingQuestion('fieldLabels', reference);
  }
  
  // Determine if field labels are needed based on question configuration
  function shouldShowFieldLabels() {
    const hasNodes = question.dataset?.some(handler => handler[0] === 'nodes');
    const isText = question.kind === 'Text';
    const isSelect = question.kind === 'Select';
    const isQuant = question.kind === 'Quant';
    const isManyToMany = question.manyToMany === true;
    const isExtendable = question.extendable === true;
    
    // Show for text/select creating nodes, or any many-to-many, or extendable questions, or quant with multiple fields
    return (hasNodes && (isText || isSelect)) || 
           isManyToMany || 
           isExtendable ||
           (isQuant && (question.questionLabels?.length > 1 || question.fieldLabels));
  }
  
  // Common reference options with descriptions
  const commonReferences = [
    { value: '__priorAnswers__', label: 'Previous Question (All Answers)', description: 'Use all answers from the previous question as field labels' },
    { value: '__priorAnswer__', label: 'Previous Question (Single Answer)', description: 'Use single answer from the previous question' },
    { value: '__answers__', label: 'This Question (All Answers)', description: 'Use this question\'s answers as labels (recursive)' }
  ];
  
  // Predefined field label templates
  const fieldTemplates = [
    { name: 'Basic Fields', labels: ['Name', 'Description', 'Category'] },
    { name: 'Contact Info', labels: ['Name', 'Email', 'Phone', 'Organization'] },
    { name: 'Location Fields', labels: ['Place Name', 'Address', 'City', 'Country'] },
    { name: 'Relationship Fields', labels: ['Person', 'Relationship Type', 'Frequency', 'Strength'] },
    { name: 'Project Fields', labels: ['Project Name', 'Role', 'Start Date', 'Status'] },
    { name: 'Rating Fields', labels: ['Aspect 1', 'Aspect 2', 'Aspect 3', 'Overall'] }
  ];
  
  function applyTemplate(template) {
    updateCustomFieldLabels([...template.labels]);
  }
  
  // Reactive derived state - automatically updates when question changes
  $: question, availableQuestions = getAvailableQuestions();
  $: question, fieldLabelsType = getFieldLabelsType();
  $: question, isReference = fieldLabelsType === 'reference';
  $: question, isCustom = fieldLabelsType === 'custom';
  $: question, hasNone = fieldLabelsType === 'none';
  $: question, showEditor = shouldShowFieldLabels();
</script>

{#if showEditor}
  <div class="fieldlabels-editor">
    <div class="fieldlabels-header">
      <label>Field Labels Configuration</label>
      <span class="fieldlabels-type-badge" class:none={hasNone} class:reference={isReference} class:custom={isCustom}>
        {fieldLabelsType.toUpperCase()}
      </span>
    </div>
    
    <!-- Field Labels Type Selector -->
    <div class="fieldlabels-type-selector">
      <div class="type-options">
        <label class="radio-option">
          <input 
            type="radio" 
            name="fieldLabelsType" 
            value="none"
            checked={hasNone}
            on:change={() => setFieldLabelsType('none')}
          />
          <span class="radio-label">No Field Labels</span>
          <span class="radio-description">Use default field numbering</span>
        </label>
        
        <label class="radio-option">
          <input 
            type="radio" 
            name="fieldLabelsType" 
            value="reference"
            checked={isReference}
            on:change={() => setFieldLabelsType('reference')}
          />
          <span class="radio-label">Dynamic Reference</span>
          <span class="radio-description">Use answers from other questions as field labels</span>
        </label>
        
        <label class="radio-option">
          <input 
            type="radio" 
            name="fieldLabelsType" 
            value="custom"
            checked={isCustom}
            on:change={() => setFieldLabelsType('custom')}
          />
          <span class="radio-label">Custom Labels</span>
          <span class="radio-description">Define specific field labels</span>
        </label>
      </div>
    </div>
    
    <!-- Reference Configuration -->
    {#if isReference}
      <div class="reference-configuration">
        <label>Reference Source</label>
        <select 
          value={question.fieldLabels}
          on:change={(e) => updateReferenceFieldLabels(e.target.value)}
          class="reference-select"
        >
          <optgroup label="Common References">
            {#each commonReferences as ref}
              <option value={ref.value}>{ref.label}</option>
            {/each}
          </optgroup>
          
          {#if availableQuestions.length > 0}
            <optgroup label="Specific Questions">
              {#each availableQuestions as q}
                <option value={q.allValue}>Question {q.index} (All Answers)</option>
                <option value={q.singleValue}>Question {q.index} (Single Answer)</option>
                <option value={q.manyValue}>Question {q.index} (Many Answers)</option>
              {/each}
            </optgroup>
          {/if}
        </select>
        
        <div class="reference-preview">
          <span class="preview-label">Current Reference:</span>
          <code class="preview-value">{question.fieldLabels}</code>
        </div>
        
        <div class="reference-description">
          {#each commonReferences as ref}
            {#if ref.value === question.fieldLabels}
              <p class="description-text">{ref.description}</p>
            {/if}
          {/each}
        </div>
      </div>
    {/if}
    
    <!-- Custom Configuration -->
    {#if isCustom}
      <div class="custom-configuration">
        <label>Custom Field Labels</label>
      
        
        <!-- Custom Labels Editor - Fixed binding -->
        <ArrayEditor 
          items={question.fieldLabels || []}
          placeholder="Field label"
          minItems={1}
          on:update={(e) => updateCustomFieldLabels(e.detail)}
        />
        
        <div class="labels-preview">
          <span class="preview-label">Preview:</span>
          <div class="preview-labels">
            {#each (question.fieldLabels || []) as label, idx}
              <span class="preview-label-item">{idx + 1}. {label || 'Untitled Field'}</span>
            {/each}
          </div>
        </div>
      </div>
    {/if}
    
    <!-- Context Information -->
    <div class="context-info">
      <h6>Field Labels Context</h6>
      <div class="context-grid">
        <div class="context-item">
          <strong>Question Type:</strong> {question.kind}
        </div>
        <div class="context-item">
          <strong>Many-to-Many:</strong> {question.manyToMany ? 'Yes' : 'No'}
        </div>
        <div class="context-item">
          <strong>Extendable:</strong> {question.extendable ? 'Yes' : 'No'}
        </div>
        {#if question.kind === 'Quant'}
          <div class="context-item">
            <strong>Question Labels:</strong> {question.questionLabels?.length || 1} fields
          </div>
        {/if}
      </div>
    </div>
    
    <!-- Help Text -->
    <div class="fieldlabels-help">
      {#if hasNone}
        <p>Fields will be numbered automatically (Field 1, Field 2, etc.)</p>
      {:else if isReference}
        <p>Field labels will be dynamically generated from the referenced question's answers.</p>
      {:else if isCustom}
        <p>Each field will use the corresponding custom label you've defined.</p>
      {/if}
    </div>
  </div>
{/if}

<style lang="scss">
  .fieldlabels-editor {
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 15px;
    background: rgba(255, 255, 255, 0.9);
  }
  
  .fieldlabels-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    
    label {
      font-weight: bold;
      font-size: 0.85rem;
      color: #374151;
    }
  }
  
  .fieldlabels-type-badge {
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: bold;
    letter-spacing: 0.5px;
    
    &.none {
      background: #6b7280;
      color: white;
    }
    
    &.reference {
      background: #3b82f6;
      color: white;
    }
    
    &.custom {
      background: #10b981;
      color: white;
    }
  }
  
  .fieldlabels-type-selector {
    margin-bottom: 20px;
    
    .type-options {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    
    .radio-option {
      display: flex;
      flex-direction: column;
      padding: 12px;
      border: 2px solid #e5e7eb;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &:hover {
        border-color: #d1d5db;
        background: #f9fafb;
      }
      
      input[type="radio"] {
        margin-bottom: 6px;
        width: auto;
      }
      
      .radio-label {
        font-weight: 600;
        font-size: 0.8rem;
        color: #374151;
        margin-bottom: 2px;
      }
      
      .radio-description {
        font-size: 0.7rem;
        color: #6b7280;
        font-style: italic;
      }
      
      input[type="radio"]:checked + .radio-label {
        color: #059669;
      }
    }
  }
  
  .reference-configuration {
    background: #eff6ff;
    border: 2px solid #3b82f6;
    border-radius: 6px;
    padding: 15px;
    margin-bottom: 15px;
    
    label {
      display: block;
      font-weight: bold;
      margin-bottom: 8px;
      font-size: 0.75rem;
      color: #1e40af;
    }
    
    .reference-select {
      width: 100%;
      border: 1px solid #3b82f6;
      border-radius: 4px;
      background: #fff;
      font-size: 0.75rem;
      font-family: monospace;
      margin-bottom: 10px;
      
      &:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 1px #2563eb;
      }
    }
    
    .reference-preview {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
      
      .preview-label {
        font-size: 0.7rem;
        color: #1e40af;
        font-weight: 500;
      }
      
      .preview-value {
        background: #dbeafe;
        padding: 2px 6px;
        border-radius: 3px;
        font-family: monospace;
        font-size: 0.7rem;
        color: #1e40af;
      }
    }
    
    .reference-description {
      .description-text {
        font-size: 0.7rem;
        color: #1e40af;
        font-style: italic;
        margin: 0;
      }
    }
  }
  
  .custom-configuration {
    background: #ecfdf5;
    border: 2px solid #10b981;
    border-radius: 6px;
    padding: 15px;
    margin-bottom: 15px;
    
    label {
      display: block;
      font-weight: bold;
      margin-bottom: 8px;
      font-size: 0.75rem;
      color: #059669;
    }
    
    .template-selector {
      margin-bottom: 15px;
      
      .template-label {
        font-size: 0.7rem;
        color: #059669;
        margin-bottom: 8px;
        display: block;
      }
      
      .template-buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        
        .btn-template {
          padding: 4px 8px;
          background: #d1fae5;
          border: 1px solid #10b981;
          border-radius: 4px;
          font-size: 0.7rem;
          color: #065f46;
          cursor: pointer;
          transition: all 0.2s ease;
          
          &:hover {
            background: #a7f3d0;
            border-color: #059669;
          }
        }
      }
    }
    
    .labels-preview {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #a7f3d0;
      
      .preview-label {
        font-size: 0.7rem;
        color: #059669;
        font-weight: 500;
        margin-bottom: 6px;
        display: block;
      }
      
      .preview-labels {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        
        .preview-label-item {
          background: #d1fae5;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 0.65rem;
          color: #065f46;
        }
      }
    }
  }
  
  .context-info {
    background: #fefce8;
    border: 1px solid #eab308;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 15px;
    
    h6 {
      margin: 0 0 8px 0;
      font-size: 0.75rem;
      font-weight: bold;
      color: #92400e;
    }
    
    .context-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 8px;
      
      .context-item {
        font-size: 0.7rem;
        color: #78350f;
        
        strong {
          color: #92400e;
        }
      }
    }
  }
  
  .fieldlabels-help {
    font-size: 0.7rem;
    color: #6b7280;
    font-style: italic;
    
    p {
      margin: 0;
    }
  }
</style>